import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Scale, RefreshCw, ClipboardList } from 'lucide-react';

export default function Ajustes() {
  const { hasRole } = useAuth();
  const [tab, setTab] = useState('nuevo');
  const [warehouses, setWarehouses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [ajustes, setAjustes] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);

  // Adjustment form
  const [warehouseId, setWarehouseId] = useState('');
  const [groupCode, setGroupCode] = useState('');
  const [invType, setInvType] = useState('');
  const [cantidad, setCantidad] = useState('');
  const [motivo, setMotivo] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [saving, setSaving] = useState(false);
  const [recomputing, setRecomputing] = useState(false);

  useEffect(() => {
    Promise.all([
      API.get('/catalogs/warehouses'),
      API.get('/catalogs/groups'),
      API.get('/ajustes'),
      API.get('/auditoria')
    ]).then(([w, g, a, au]) => {
      setWarehouses(w.data);
      setGroups(g.data);
      setAjustes(a.data);
      setAuditLogs(au.data);
    }).catch(() => toast.error('Error cargando datos'));
  }, []);

  const handleAjuste = async () => {
    if (!warehouseId || !groupCode || !invType || !cantidad || !motivo) {
      toast.error('Completa todos los campos. El motivo es obligatorio.');
      return;
    }
    if (motivo.length < 5) {
      toast.error('El motivo debe tener al menos 5 caracteres');
      return;
    }
    setSaving(true);
    try {
      const res = await API.post('/ajustes', {
        warehouse_id: warehouseId,
        group_code: groupCode,
        inventory_type: invType,
        cantidad: parseInt(cantidad),
        motivo,
        observaciones
      });
      toast.success(`Ajuste aplicado. Nuevo saldo: ${res.data.saldo_nuevo}`);
      setCantidad(''); setMotivo(''); setObservaciones('');
      // Refresh lists
      const [a, au] = await Promise.all([API.get('/ajustes'), API.get('/auditoria')]);
      setAjustes(a.data); setAuditLogs(au.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally { setSaving(false); }
  };

  const handleRecompute = async () => {
    if (!window.confirm('¿Recalcular todos los saldos desde movimientos? Esta operación puede tardar.')) return;
    setRecomputing(true);
    try {
      const res = await API.post('/admin/recompute-balances');
      const data = res.data;
      if (data.diferencias_encontradas > 0) {
        toast.warning(`Recálculo completado. Se encontraron ${data.diferencias_encontradas} diferencias.`);
      } else {
        toast.success('Recálculo completado. Los saldos son consistentes.');
      }
      const au = await API.get('/auditoria');
      setAuditLogs(au.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally { setRecomputing(false); }
  };

  const TYPES = [
    { value: 'usado', label: 'Usados' },
    { value: 'gallo', label: 'Gallos' },
    { value: 'casco', label: 'Cascos' },
    { value: 'merma', label: 'Merma' },
    { value: 'en_revision', label: 'En Revisión' },
    { value: 'en_tarima', label: 'En Tarima' }
  ];

  const canAdjust = hasRole('admin', 'supervisor');
  const canRecompute = hasRole('admin');

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Ajustes y Auditoría</h1>
          <p className="text-sm text-muted-foreground">Ajustes de inventario, recálculo y registro de auditoría</p>
        </div>
        {canRecompute && (
          <Button onClick={handleRecompute} disabled={recomputing} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50">
            <RefreshCw className={`h-4 w-4 mr-1 ${recomputing ? 'animate-spin' : ''}`} />
            {recomputing ? 'Recalculando...' : 'Recalcular Saldos'}
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {canAdjust && <TabsTrigger value="nuevo">Nuevo Ajuste</TabsTrigger>}
          <TabsTrigger value="historial">Historial de Ajustes</TabsTrigger>
          <TabsTrigger value="auditoria">Auditoría</TabsTrigger>
        </TabsList>

        {canAdjust && (
          <TabsContent value="nuevo" className="mt-4">
            <Card className="bg-white border">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Scale className="h-4 w-4" /> Ajuste de Inventario
                </CardTitle>
                <p className="text-xs text-amber-600">Los ajustes quedan registrados en la auditoría y no se pueden borrar.</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-sm font-medium">Almacén *</label>
                    <Select value={warehouseId} onValueChange={setWarehouseId}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                      <SelectContent>{warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.nombre}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Grupo *</label>
                    <Select value={groupCode} onValueChange={setGroupCode}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                      <SelectContent>{groups.map(g => <SelectItem key={g.codigo} value={g.codigo}>{g.codigo}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Tipo de Inventario *</label>
                    <Select value={invType} onValueChange={setInvType}>
                      <SelectTrigger className="mt-1"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                      <SelectContent>{TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium">Cantidad * (positiva para agregar, negativa para quitar)</label>
                    <Input type="number" value={cantidad} onChange={(e) => setCantidad(e.target.value)} placeholder="Ej: 5 o -3" className="mt-1" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Motivo * (mínimo 5 caracteres)</label>
                    <Input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej: Conteo físico detectó faltante" className="mt-1" />
                  </div>
                </div>
                <Input value={observaciones} onChange={(e) => setObservaciones(e.target.value)} placeholder="Observaciones adicionales (opcional)" />
                <Button onClick={handleAjuste} disabled={saving} className="bg-[#0F6E87] hover:bg-[#0C5D73]">
                  <Scale className="h-4 w-4 mr-1" /> {saving ? 'Aplicando...' : 'Aplicar Ajuste'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="historial" className="mt-4">
          <Card className="bg-white border"><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow className="bg-muted/30">
                <TableHead>Fecha</TableHead><TableHead>Grupo</TableHead><TableHead>Tipo</TableHead>
                <TableHead className="text-right">Cantidad</TableHead><TableHead>Observaciones</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {ajustes.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Sin ajustes registrados</TableCell></TableRow>}
                {ajustes.map((a, i) => (
                  <TableRow key={i} className="hover:bg-muted/40">
                    <TableCell className="text-xs">{new Date(a.fecha).toLocaleString('es-MX')}</TableCell>
                    <TableCell className="font-medium">{a.group_code}</TableCell>
                    <TableCell className="capitalize">{a.inventory_type_destiny || a.inventory_type_origin}</TableCell>
                    <TableCell className="text-right font-semibold">{a.cantidad}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[300px] truncate">{a.observaciones}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="auditoria" className="mt-4">
          <Card className="bg-white border"><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow className="bg-muted/30">
                <TableHead>Fecha</TableHead><TableHead>Acción</TableHead><TableHead>Módulo</TableHead>
                <TableHead>Referencia</TableHead><TableHead>Antes</TableHead><TableHead>Después</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {auditLogs.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Sin registros de auditoría</TableCell></TableRow>}
                {auditLogs.map((l, i) => (
                  <TableRow key={i} className="hover:bg-muted/40">
                    <TableCell className="text-xs">{new Date(l.created_at).toLocaleString('es-MX')}</TableCell>
                    <TableCell><span className="px-2 py-0.5 rounded bg-muted text-xs">{l.accion}</span></TableCell>
                    <TableCell className="capitalize">{l.modulo}</TableCell>
                    <TableCell className="text-xs max-w-[200px] truncate">{l.referencia}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{l.datos_antes_json ? JSON.stringify(l.datos_antes_json).substring(0, 50) : '-'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{l.datos_despues_json ? JSON.stringify(l.datos_despues_json).substring(0, 50) : '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
